import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { AreaValues } from 'components/organisms/AreaCreationModal/AreaCreationModal';
import { t } from 'i18next';
import { Box, CardHeader, List, Typography, Stack, useMediaQuery, Theme } from '@mui/material';
import { IconEdit } from '@tabler/icons-react';
import { Alert, Button, PropertyListItem, StrongModalText } from '@atoms';
import { PERMISSIONS } from '@globalConstants';
import {
  useAssignBalanceToArea,
  useEditArea,
  useModal,
  usePermissions,
  useSelectedCustomerAreas,
  useShowAreaFeature,
} from '@hooks';
import { AreaCreationModal, FormModal, RetrieveBalanceModal } from '@organisms';
import { parseAmount } from '@utils';
import { Area } from '@modules/clients/modules/CompanyDetails/components/AreasList/AreasList';
import {
  AreaCardProps,
  AreaInfo,
  CONFIG_ASSIGN_BALANCE_TO_AREA_FIELD,
  ROWS,
  RowItem,
  VALIDATION_ASSIGN_BALANCE_TO_AREA_SCHEMA,
} from './constants';
import useBalanceReassigmentToArea from './hooks/BalanceReassigmentToArea';
import { styles, propertyListItemStyle } from './styles';

/**
 * Area details component
 */
const AreaCardDetail = ({ area, fetchAreas, fetchCompany, fetchMovements }: AreaCardProps) => {
  const [addAreaModalOpen, setAddAreaModalOpen] = useState(false);
  const { isLoading: isEditAreaLoading, editArea } = useEditArea();
  const responsive = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const {
    isLoading: isAssignBalanceLoading,
    submit: submitAssignBalance,
    isAssignBalanceModalOpen,
    closeModal,
    openModal,
    closeAlert,
    showAssignBalanceAlert,
    values,
    handleSetValues,
    closeSuccessAlert,
    showSuccessAlert,
  } = useAssignBalanceToArea(() => {
    fetchAreas();
    fetchCompany();
  });
  const { retrieveBalance, postRetrieveBalance, closeSuccessAlertHandler } =
    useBalanceReassigmentToArea(() => {
      fetchAreas();
      fetchCompany();
      fetchMovements();
    });

  const {
    closeModal: closeRecoverAreaModal,
    open: isRecoverAreaModalOpen,
    openModal: openRecoverAreaModal,
  } = useModal<Area>();
  const isDemo = usePermissions([PERMISSIONS.demoUpdateRecoveryBalance]);
  const isCompanyDetail = 'isCompany' in area;
  const { data: areas } = useSelectedCustomerAreas();
  const { showAreaFeature } = useShowAreaFeature();
  const areaName = useMemo(
    () => areas.find((item: Area) => item.id === values.areaId)?.fantasyName,
    [areas, values]
  );
  /**
   * Render area row item
   */
  const renderRow = (row: RowItem<AreaInfo>) => {
    const rowValue = row.value(area);

    if (row.shouldHideItem && row.shouldHideItem(area)) return null;

    const rowValueElement = (
      <Typography variant='body1' sx={styles.value}>
        {rowValue ?? '-'}
      </Typography>
    );

    return (
      <Box key={`Box-${row.label}`}>
        <PropertyListItem
          label={row.label}
          value={rowValueElement}
          customStyle={propertyListItemStyle}
        />
      </Box>
    );
  };

  return (
    <Box sx={styles.container(responsive)}>
      <CardHeader
        title={
          Boolean(area?.balance) || area?.balance === 0 ? parseAmount(Number(area.balance)) : '-'
        }
        sx={styles.padding0}
        action={
          !isCompanyDetail && (
            <Button
              customStyle={{ box: styles.editButton }}
              startIcon={<IconEdit />}
              size='small'
              variant='outlined'
              text={'CLIENTS:CREATE_AREA:EDIT'}
              onClick={() => setAddAreaModalOpen(true)}
            />
          )
        }
      />
      <Box sx={styles.padding0}>
        <Stack spacing={3}>
          <List>{ROWS.map(renderRow)}</List>
        </Stack>
        {showAreaFeature && isCompanyDetail ? (
          <Button
            size='small'
            variant='outlined'
            text={'CLIENTS:ASSIGN_BALANCE'}
            onClick={openModal}
            disabled={Number(area?.balanceAvailable) <= 0}
          />
        ) : (
          <Button
            size='small'
            variant='outlined'
            text={'CLIENTS:RECOVER_BALANCE'}
            onClick={openRecoverAreaModal}
            disabled={Number(area?.balance) <= 0}
          />
        )}
      </Box>
      <RetrieveBalanceModal
        {...{
          onClose: closeRecoverAreaModal,
          totalBalance: Number(area.balance),
          isOpen: isRecoverAreaModalOpen,
          openModal: openRecoverAreaModal,
          submit: async (amount) => {
            !isCompanyDetail && (await postRetrieveBalance({ amount, areaId: area.id }));
          },
          isLoading: retrieveBalance.loading,
          isAreaRecover: true,
          areaName: !isCompanyDetail ? area.fantasyName : '',
        }}
      />
      <FormModal
        title={'CLIENTS:ASSIGN_BALANCE'}
        isLoading={isAssignBalanceLoading}
        isOpen={isAssignBalanceModalOpen}
        validationSchema={VALIDATION_ASSIGN_BALANCE_TO_AREA_SCHEMA(
          isCompanyDetail ? Number(area.balanceAvailable) : 0
        )}
        configFields={CONFIG_ASSIGN_BALANCE_TO_AREA_FIELD(
          isCompanyDetail ? Number(area.balanceAvailable) : 0
        )}
        onClose={closeModal}
        initialValues={{ areaId: '', amount: '' }}
        showClearFilter={false}
        onSubmit={handleSetValues}
        disableConfirm={isDemo}
      />
      <Alert
        variant='warning'
        modalVisible={showAssignBalanceAlert}
        handleClose={closeAlert}
        handleSubmit={submitAssignBalance}
        closeButtonText='COMMON:CANCEL'
        title={t('DISPERSIONS:MODAL_TITLE')}
        submitLoading={isAssignBalanceLoading}
        content={
          <Typography color='gray.90'>
            <Trans
              i18nKey={t('CLIENTS:ASSIGN_BALANCE_MODAL:CONFIRMATION_DESCRIPTION')}
              components={{ bold: <StrongModalText /> }}
              values={{
                amount: parseAmount(Number(values.amount.replace(',', '.'))),
                areaName: areaName,
              }}
            />
          </Typography>
        }
      />
      <Alert
        variant='success'
        modalVisible={showSuccessAlert}
        handleClose={closeSuccessAlert}
        closeButtonText='COMMON:UNDERSTOOD'
        title={t('CLIENTS:ASSIGN_BALANCE_MODAL:SUCCESS_TITLE')}
        description={t('CLIENTS:ASSIGN_BALANCE_MODAL:SUCCESS_DESCRIPTION')}
      />
      <AreaCreationModal
        isEditing
        isOpen={addAreaModalOpen}
        isLoading={isEditAreaLoading}
        onSubmit={async (values: AreaValues) => {
          await editArea({
            areaId: String(area.id),
            areaName: values.areaName,
            costCenter: values.costCenter ?? undefined,
          });
          fetchAreas();
          setAddAreaModalOpen(false);
        }}
        closeModal={() => setAddAreaModalOpen(false)}
        initialValues={
          !isCompanyDetail
            ? { areaName: area.fantasyName, costCenter: area.costCenter ?? '' }
            : undefined
        }
      />
      <Alert
        variant='success'
        modalVisible={retrieveBalance.showSuccessAlert}
        handleClose={closeSuccessAlertHandler}
        closeButtonText='COMMON:UNDERSTOOD'
        title={t('CLIENTS:REASSIGN_BALANCE_MODAL:SUCCESS_TITLE')}
        description={t('CLIENTS:REASSIGN_BALANCE_MODAL:SUCCESS_DESCRIPTION')}
      />
    </Box>
  );
};

export default AreaCardDetail;
